// shared-components/services/ActivityLogService.js

import { supabase } from "@shared/core/services/SupabaseService";

class ActivityLogService {
  constructor() {
    this.websiteId = null;
    this.userId = null;
    this.userEmail = null;
  }

  async initialize() {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      this.userId = user.id;
      this.userEmail = user.email;
    }

    // Get website ID
    this.websiteId = import.meta.env.VITE_WEBSITE_ID;
  }

  async logActivity(
    action,
    resourceType,
    resourceId = null,
    resourceName = null,
    details = {},
  ) {
    try {
      // Ensure we're initialized
      if (!this.userId) {
        await this.initialize();
      }

      if (!this.userId || !this.websiteId) {
        const errorMsg = `Cannot log activity: missing ${!this.userId ? "user ID" : "website ID"}`;
        console.error(errorMsg, {
          userId: this.userId,
          websiteId: this.websiteId,
          action,
          resourceType,
        });
        throw new Error(errorMsg);
      }

      // Prepare insert data
      const insertData = {
        website_id: this.websiteId,
        user_id: this.userId,
        user_email: this.userEmail,
        action,
        resource_type: resourceType,
        resource_id: resourceId?.toString() || null,
        resource_name: resourceName,
        details,
      };

      // Perform insert and request data back to verify
      const { data, error } = await supabase
        .from("activity_logs")
        .insert(insertData)
        .select();

      if (error) {
        console.error("Error logging activity:", error);
        throw error;
      }

      // Check for silent RLS denial (INSERT succeeded but SELECT filtered by RLS)
      if (!data || data.length === 0) {
        console.error(
          "🔍 INSERT succeeded but returned no data - RLS may be blocking SELECT!",
        );
        throw new Error(
          "Activity log insert succeeded but no rows returned (RLS filtering?)",
        );
      }
    } catch (error) {
      console.error("Error in logActivity:", error);
      throw error;
    }
  }

  // Convenience methods for common actions
  async logCreate(resourceType, resourceId, resourceName, details = {}) {
    return this.logActivity(
      "created",
      resourceType,
      resourceId,
      resourceName,
      details,
    );
  }

  async logUpdate(resourceType, resourceId, resourceName, details = {}) {
    return this.logActivity(
      "updated",
      resourceType,
      resourceId,
      resourceName,
      details,
    );
  }

  async logDelete(resourceType, resourceId, resourceName, details = {}) {
    return this.logActivity(
      "deleted",
      resourceType,
      resourceId,
      resourceName,
      details,
    );
  }

  async logStatusChange(
    resourceType,
    resourceId,
    resourceName,
    oldStatus,
    newStatus,
  ) {
    return this.logActivity(
      "status_changed",
      resourceType,
      resourceId,
      resourceName,
      {
        old_status: oldStatus,
        new_status: newStatus,
      },
    );
  }
}

export const activityLogService = new ActivityLogService();
